import { useState, type ReactNode } from "react";
import { Icon } from "@/components/icons";
import { Tabs } from "@/components/tabs";
import { CountryTag } from "@/components/country-tag";
import { Modal } from "@/components/modal";
import { Button } from "@/components/button";
import { ScoreChip } from "@/components/score-chip";

export { ScoreChip as ScorePill };

export type CardItem =
  | { kind: "row"; icon: string; label: string; value: ReactNode }
  | { kind: "block"; icon: string; label: string; items: ReactNode[] };

export type CardConfig = {
  /** Section header icon. */
  icon: string;
  /** Section header title. */
  title: string;
  items: CardItem[];
};

export type ArticleContent = {
  /** External source URL — wired to "Open original ↗". */
  url: string;
  /** Display domain, e.g. "msn.com". */
  domain: string;
  /** ISO display date, e.g. "2026-02-18". */
  publishedAt: string;
  /** ISO language code, e.g. "DE". */
  language: string;
  /** Image alt / placeholder caption. */
  imageAlt?: string;
  /** Article keywords rendered as chips. */
  keywords: string[];
  /** Paragraphs of body text. Already sanitized; rendered as plain <p>. */
  body: string[];
};

export type MatchVariant = {
  slug: string;
  /** Label shown in the index list. */
  label: string;
  /** Material Symbols icon next to the name in the header. */
  headerIcon: string;
  /** Big title shown next to the icon. */
  name: string;
  /** Source chip text (e.g. "PEP", "ADV-MEDIA"). */
  source: string;
  /** Three cards rendered side-by-side in the General tab. */
  cards: [CardConfig, CardConfig, CardConfig];
  /** Adverse-media payload — surfaces a "Read article" button + modal. */
  article?: ArticleContent;
};

const TAB_ITEMS = [
  { id: "general", label: "General" },
  { id: "additional", label: "Additional info" },
  { id: "remarks", label: "Remarks" },
];

export function MatchDetailShowcase({ variant }: { variant: MatchVariant }) {
  const [tab, setTab] = useState<string>("general");
  const [articleOpen, setArticleOpen] = useState(false);

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      <div className="card @container flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
        <DetailBreadcrumb name={variant.name} />
        <DetailHeader variant={variant} />
        {/* Tabs + article actions: stacked on phones (the two action buttons
            would otherwise overflow past the tabs), one row from @2xl. */}
        <div className="flex flex-col gap-2 @2xl:flex-row @2xl:items-center @2xl:justify-between @2xl:gap-3">
          <Tabs tabs={TAB_ITEMS} value={tab} onChange={setTab} />
          {variant.article && (
            <ArticleActions article={variant.article} onRead={() => setArticleOpen(true)} />
          )}
        </div>

        <div className="@container flex min-h-0 flex-1 items-stretch gap-1 overflow-hidden rounded-[8px] border border-grey-200 bg-grey-100 p-1">
          {tab === "general" && (
            // Cards stack vertically on phones/tablets and only sit side-by-side
            // from @3xl — matches the live match-detail GeneralPanel so columns
            // never squish/clip on narrow viewports (device-responsive).
            <div className="flex flex-col @3xl:flex-row @3xl:items-stretch flex-1 min-w-0 gap-1 overflow-y-auto">
              {variant.cards.map((card) => (
                <DataCard key={card.title}>
                  <SectionHeader icon={card.icon} title={card.title} />
                  {card.items.map((item, i) => (
                    <CardRow key={i} item={item} />
                  ))}
                </DataCard>
              ))}
            </div>
          )}
          {tab === "additional" && (
            <PlaceholderPanel label="Additional info — not detailed in this preview." />
          )}
          {tab === "remarks" && (
            <PlaceholderPanel label="Remarks — not detailed in this preview." />
          )}
        </div>
      </div>
      {variant.article && (
        <ArticleModal
          variant={variant}
          article={variant.article}
          open={articleOpen}
          onClose={() => setArticleOpen(false)}
        />
      )}
    </section>
  );
}

function DetailBreadcrumb({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-1 text-body font-medium">
      <a
        href="/preview/match-detail"
        aria-label="Back to variants"
        className="flex size-5 items-center justify-center text-grey-900 hover:text-black"
      >
        <Icon name="arrow_back" size={20} />
      </a>
      <a href="/preview/match-detail" className="text-grey-500 hover:text-grey-900">
        Match-detail variants
      </a>
      <span className="text-grey-300">/</span>
      <span className="truncate text-black" aria-current="page">
        {name}
      </span>
    </div>
  );
}

function DetailHeader({ variant }: { variant: MatchVariant }) {
  return (
    <div className="flex w-full items-center gap-2">
      <div className="flex size-9 shrink-0 items-center justify-center rounded-[4px] bg-grey-100 text-grey-900">
        <Icon name={variant.headerIcon} size={20} />
      </div>
      <h3 className="truncate text-h4 text-black">{variant.name}</h3>
      <SourceBadge source={variant.source} />
    </div>
  );
}

function ArticleActions({ article, onRead }: { article: ArticleContent; onRead: () => void }) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <Button
        hierarchy="secondary"
        leftIcon={<Icon name="link" size={20} />}
        onClick={() => window.open(article.url, "_blank", "noopener,noreferrer")}
      >
        Open original
      </Button>
      <Button hierarchy="primary" leftIcon={<Icon name="article" size={20} />} onClick={onRead}>
        Read archived copy
      </Button>
    </div>
  );
}

function SourceBadge({ source }: { source: string }) {
  const isAdvMedia = source === "ADV-MEDIA";
  return (
    <span
      className={[
        "inline-flex shrink-0 items-center gap-1 rounded-[4px] px-2 py-1 text-small font-medium",
        isAdvMedia ? "bg-yellow-500 text-white" : "bg-grey-200 text-grey-900",
      ].join(" ")}
    >
      {source}
      <Icon name="info" size={20} />
    </span>
  );
}

function CardRow({ item }: { item: CardItem }) {
  if (item.kind === "row") {
    return (
      <DataRow icon={item.icon} label={item.label}>
        <span className="font-medium break-words text-black">{item.value}</span>
      </DataRow>
    );
  }
  return (
    <DataBlock icon={item.icon} label={item.label}>
      <BulletList items={item.items} />
    </DataBlock>
  );
}

function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-grey-200 pb-2 mb-1 text-small">
      <Icon name={icon} size={20} className="shrink-0 text-grey-900" />
      <span className="font-medium text-black">{title}</span>
    </div>
  );
}

function DataCard({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 min-w-0 flex-col rounded-[4px] border border-grey-200 bg-white p-4 shadow-flat">
      {children}
    </div>
  );
}

function DataRow({ icon, label, children }: { icon: string; label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 border-b border-grey-100 py-1.5 last:border-b-0 text-small">
      <Icon name={icon} size={20} className="shrink-0 text-grey-900" />
      <span className="text-grey-900">{label}</span>
      {children}
    </div>
  );
}

function DataBlock({
  icon,
  label,
  children,
}: {
  icon: string;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-grey-100 py-1.5 last:border-b-0 text-small">
      <div className="flex items-center gap-2">
        <Icon name={icon} size={20} className="shrink-0 text-grey-900" />
        <span className="font-medium text-black">{label}</span>
      </div>
      {children}
    </div>
  );
}

function BulletList({ items }: { items: ReactNode[] }) {
  if (items.length === 0) {
    return <p className="ml-7 text-grey-500">—</p>;
  }
  return (
    <ul className="ml-7 flex list-disc flex-col gap-1 text-grey-900 marker:text-grey-500">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

function PlaceholderPanel({ label }: { label: string }) {
  return (
    <div className="flex flex-1 min-w-0 items-center justify-center rounded-[4px] border border-grey-200 bg-white p-4 text-small text-grey-500 shadow-flat">
      {label}
    </div>
  );
}

function ArticleModal({
  variant,
  article,
  open,
  onClose,
}: {
  variant: MatchVariant;
  article: ArticleContent;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Modal open={open} onClose={onClose} title={variant.name} width={720}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-small text-grey-600">
          <SourceBadge source={variant.source} />
          <span>{article.domain}</span>
          <span aria-hidden className="text-grey-300">
            ·
          </span>
          <span>Published {article.publishedAt}</span>
          <span aria-hidden className="text-grey-300">
            ·
          </span>
          <span>{article.language}</span>
        </div>

        <div className="flex flex-col gap-3 text-body text-black">
          {article.body.map((paragraph, i) => (
            <p key={i} className="leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>

        {article.keywords.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            {article.keywords.map((keyword) => (
              <span
                key={keyword}
                className="inline-flex items-center rounded-[4px] bg-grey-100 px-2 py-0.5 text-tiny font-medium text-grey-900"
              >
                {keyword}
              </span>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

/* ---------- shared cell renderers used by variant data ---------- */

export function FlagInline({ country }: { country: string }) {
  return (
    <span className="font-medium text-black">
      <CountryTag country={country} />
    </span>
  );
}

export function SourceLink({ source }: { source: string }) {
  return <span className="font-medium text-black">{source}</span>;
}

export function ExternalLink({ href, display }: { href: string; display: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-0.5 font-medium text-green-700 hover:underline"
    >
      {display}
      <Icon name="arrow_outward" size={20} />
    </a>
  );
}
